import { defineStore } from 'pinia';
import { ref } from 'vue';
import { listDiveCenters } from '@/api/dive-centers';
import type { DiveCenter } from '@/types';

/**
 * Clave del centro seleccionado.
 *
 * Se persiste porque el menú lateral es plano también en las cuentas company
 * (§18.5 rehecho): las secciones del centro cuelgan del centro actual, y hay
 * pantallas de la CUENTA —Dashboard, Clients, Settings— cuya URL no lleva
 * `:id`. Sin recordar la selección, entrar a cualquiera de ellas dejaba al menú
 * sin saber de qué centro hablar.
 *
 * La URL sigue mandando cuando trae `:id`; esto solo cubre el hueco.
 */
const SELECTED_KEY = 'bt_provider_dive_center';

/**
 * Shared dive-center list, used by the sidebar tree (and anywhere else that
 * needs the centers without re-fetching). The list views still fetch their own
 * data; this store is the lightweight source for navigation. Call `load(true)`
 * after a create/delete so the sidebar tree stays in sync.
 */
export const useDiveCentersStore = defineStore('diveCenters', () => {
    const list = ref<DiveCenter[]>([]);
    const loaded = ref(false);
    const loading = ref(false);

    /*
     * Carga en curso. Se guarda para que un segundo `await load()` espere a la
     * primera en vez de volver enseguida con la lista todavía vacía: el layout
     * la dispara al montar y las páginas la piden casi al mismo tiempo, así que
     * la carrera es la norma, no el caso raro.
     */
    let pending: Promise<void> | null = null;

    /** Último centro elegido a mano. Ver `useCurrentDiveCenter` para la precedencia. */
    const selectedId = ref<string | null>(localStorage.getItem(SELECTED_KEY));

    function select(id: string | null) {
        if (id === selectedId.value) return;
        selectedId.value = id;
        if (id) localStorage.setItem(SELECTED_KEY, id);
        else localStorage.removeItem(SELECTED_KEY);
    }

    async function load(force = false) {
        if (pending) return pending;
        if (loaded.value && !force) return;

        loading.value = true;
        pending = run();
        try {
            await pending;
        } finally {
            pending = null;
        }
    }

    async function run() {
        try {
            const res = await listDiveCenters();
            list.value = res.diveCenters;
            loaded.value = true;

            /*
             * El id guardado puede apuntar a un centro borrado, o al de otra
             * cuenta si alguien inicia sesión con otro usuario en el mismo
             * navegador. Se limpia acá, con la lista fresca en la mano, para no
             * arrastrar una selección fantasma por toda la app.
             */
            if (selectedId.value && !list.value.some(dc => dc.id === selectedId.value))
                select(null);
        } catch {
            // Best-effort: a failed sidebar fetch shouldn't break navigation.
        } finally {
            loading.value = false;
        }
    }

    function reset() {
        list.value = [];
        loaded.value = false;
        select(null);
    }

    return { list, loaded, loading, selectedId, select, load, reset };
});
